import { BellFilled } from "@ant-design/icons";
import { Badge, Empty, Popover, Segmented, Spin, Tooltip, message } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/vi";
import relativeTime from "dayjs/plugin/relativeTime";
import { chain } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { countUnread, getNotifications, readNotification } from "../../apis/notificationApi";
import { NOTIFICATION_TYPE, ROUTES } from "../../common/constants";

dayjs.extend(relativeTime);

const LIMIT = 10;

const ICON_STORE = {
  RMP: "/assets/img/icons/ads.png",
  DEEPLINK: "/assets/img/icons/deeplink.png",
  ACCOUNT: "/assets/img/icons/account.png",
};

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(NOTIFICATION_TYPE.INBOX);
  const [notifications, setNotifications] = useState({ [NOTIFICATION_TYPE.NEWS]: [], [NOTIFICATION_TYPE.INBOX]: [] });
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState({ [NOTIFICATION_TYPE.NEWS]: LIMIT, [NOTIFICATION_TYPE.INBOX]: LIMIT });
  const [total, setTotal] = useState({ [NOTIFICATION_TYPE.NEWS]: 0, [NOTIFICATION_TYPE.INBOX]: 0 });
  const [count, setCount] = useState(0);

  const history = useHistory();

  const fetchMoreNotifications = async (type, force) => {
    if (!force && (loading || notifications[type].length >= total[type])) return;
    setLoading(true);
    try {
      const { dataArray, pageable } = await getNotifications(limit[type], 0, type);
      setNotifications((prev) => ({
        ...prev,
        [type]: chain([...dataArray, ...prev[type]])
          .uniqBy("id")
          .orderBy("created_at", "desc")
          .value(),
      }));
      setLimit((prev) => ({ ...prev, [type]: prev[type] + LIMIT }));
      setTotal((prev) => ({ ...prev, [type]: pageable.totalElements }));
      setLoading(false);
    } catch (error) {
      message.error(t("An error occurred while retrieving data"));
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      fetchMoreNotifications(activeTab);
    }
  };

  const fetchCountUnread = useCallback(async () => {
    try {
      const { data } = await countUnread();
      setCount(data?.count);
    } catch {}
  }, []);

  useEffect(() => {
    fetchCountUnread();
    const interval = setInterval(() => {
      fetchCountUnread();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchCountUnread]);

  const onClickBell = () => {
    if (!open) {
      fetchMoreNotifications(activeTab, true);
    }
    setOpen(!open);
  };

  const onChangeTab = (value) => {
    setActiveTab(value);
    fetchMoreNotifications(value, true);
  };

  const onClickNotification = async (noti) => {
    setOpen(false);
    const { params, is_read } = noti;
    const { screen_code } = params || {};
    if (ROUTES[screen_code]) {
      history.push(ROUTES[screen_code], { notif: params });
    }
    if (!is_read) {
      setCount((prev) => prev - 1);
      await readNotification({ id: noti.id });
      fetchMoreNotifications(activeTab, true);
    }
  };

  const renderNotifications = (type) => {
    const notificationByType = notifications[type] || [];
    return (
      <div className="h-[72vh] overflow-auto !pl-4 !pr-1.5" onScroll={handleScroll}>
        {notificationByType.length > 0 ? (
          <>
            {notificationByType.map((item, index) => {
              const dateVisible =
                index === 0 || !dayjs(item.created_at).isSame(dayjs(notificationByType[index - 1].created_at), "date");
              return (
                <div key={item.id} className="cursor-pointer" onClick={() => onClickNotification(item)}>
                  {dateVisible && (
                    <p className="text-xs font-semibold text-tgray !mt-3 mb-0.5">
                      {dayjs(item.created_at).format("DD/MM/YYYY")}
                    </p>
                  )}
                  <Tooltip
                    mouseEnterDelay={1}
                    title={
                      <div className="text-center">
                        <p className="font-medium text-sm">{item.title}</p>
                        <p className="text-xs" dangerouslySetInnerHTML={{ __html: item.description }}></p>
                      </div>
                    }
                  >
                    <div
                      key={item.id}
                      className="relative flex items-center gap-3 px-2 py-1.5 hover:bg-gray-100 rounded-lg mb-1"
                      style={!item.is_read ? { background: "#1677ff0d" } : undefined}
                    >
                      <div className="absolute -left-3 text-lg text-antd-primary">{!item.is_read ? "•" : ""}</div>
                      <img
                        src={ICON_STORE[item.params?.module] || "/assets/img/brand/logo_icon.png"}
                        alt={item.title}
                        className="w-10 h-10 rounded-md object-cover"
                      />
                      <div>
                        <p className="font-medium text-sm line-clamp-1">{item.title}</p>
                        <p className="text-xs line-clamp-1" dangerouslySetInnerHTML={{ __html: item.description }}></p>
                        <p className="text-[11px] leading-4 text-tgray line-clamp-1">
                          {dayjs(item.created_at).locale(i18n.language).fromNow()}
                        </p>
                      </div>
                    </div>
                  </Tooltip>
                </div>
              );
            })}
            {loading && (
              <div className="text-center p-2">
                <Spin size="small" />
              </div>
            )}
          </>
        ) : (
          <div className="h-[calc(100%-8px)] flex-center rounded-lg">
            {loading ? <Spin size="small" /> : <Empty description={false} />}
          </div>
        )}
      </div>
    );
  };

  return (
    <Popover
      content={
        <div className="max-w-[100vw] sm:w-96">
          <div className="text-center font-medium mb-2 !px-3">{t("Notification")}</div>
          <div className="text-center !px-14">
            <Segmented
              options={[
                { label: t("Inbox"), value: NOTIFICATION_TYPE.INBOX },
                { label: t("News"), value: NOTIFICATION_TYPE.NEWS },
              ]}
              value={activeTab}
              onChange={(value) => onChangeTab(value)}
              block
            />
          </div>
          <div className="!mt-3">{renderNotifications(activeTab)}</div>
        </div>
      }
      trigger="click"
      arrow
      open={open}
      onOpenChange={setOpen}
      overlayInnerStyle={{ padding: "12px 0" }}
    >
      <Badge
        count={count}
        size="small"
        overflowCount={9}
        offset={[-2, 4]}
        className="cursor-pointer [&_.ant-badge-count]:!px-1"
        onClick={() => onClickBell()}
      >
        <BellFilled className="text-white text-xl" />
      </Badge>
    </Popover>
  );
}
